import React, {useState, useEffect, useRef, useCallback} from 'react';
import axios from 'axios';
import {Link, useLocation} from 'react-router-dom';

export const Categories = [
    {
        name: "All Games",
        icon: "/icons/category/all-games.svg",
        tag: "all-games",
        description:
            "Explore an extensive collection of 100+ online games across various genres, including action, adventure, puzzle, sports, and strategy. Whether you're looking for fast-paced excitement or a relaxing challenge, this category offers endless entertainment. Discover trending, classic, and fan-favorite games all in one place, ensuring there’s something for everyone.",
    },
    {
        name: "New Releases",
        icon: "/icons/category/new-release.svg",
        tag: "new-releases",
        description:
            "Stay ahead of the gaming curve with the latest and most exciting new releases. Discover fresh gameplay mechanics, stunning graphics, and immersive storylines in newly launched titles. Whether you love action-packed adventures, brain-teasing puzzles, or competitive multiplayer games, this category ensures you never miss out on the hottest new games.",
    },
    {
        name: "Top 10 Games",
        icon: "/icons/category/top-10-games.svg",
        tag: "top-10-games",
        description:
            "Play the best of the best! This category features the top 10 most popular and highly-rated games, curated based on player reviews, engagement, and overall excitement. Whether you're looking for trending action games, mind-bending puzzles, or competitive multiplayer experiences, these top-rated games promise hours of thrilling entertainment.",
    },
    {
        name: "Premium Games",
        icon: "/icons/category/premium-game.svg",
        tag: "vip-games",
        description:
            "Enjoy a high-quality gaming experience with our premium games collection. These exclusive titles offer enhanced graphics, immersive gameplay, and unique features that provide a top-tier gaming experience. Dive into thrilling adventures, challenging puzzles, and engaging multiplayer matches that take online gaming to the next level.",
    },
    {
        name: "Leaderboard Games",
        icon: "/icons/category/games-with-leaderboard.svg",
        tag: "games-with-leaderboard",
        description:
            "Compete for the top spot in our leaderboard games! Challenge yourself and other players worldwide in high-score battles, timed challenges, and competitive gameplay. Whether you love strategy, action, or puzzle games, climb the ranks and prove your skills in these exciting leaderboard-driven games.",
    },
    {
        name: "Train your Brain",
        icon: "/icons/category/brain.svg",
        tag: "train-your-brain",
        description:
            "Sharpen your mind with brain-training games that test your logic, memory, and problem-solving skills. From tricky puzzles to strategic challenges, these games are designed to keep you mentally active while having fun. Perfect for players who enjoy a mix of entertainment and cognitive workouts.",
    },
    {
        name: "Soothing",
        icon: "/icons/category/soothing.svg",
        tag: "soothing",
        description:
            "Relax and unwind with calming and stress-free games designed to bring peace to your mind. Enjoy soothing visuals, gentle music, and simple yet engaging gameplay that helps you de-stress after a long day. These games are perfect for a peaceful gaming experience.",
    },
    {
        name: "Quick Break at Work",
        icon: "/icons/category/quick-break.svg",
        tag: "quick-break-at-work",
        description:
            "Need a short gaming session during your work break? This category features fun and quick games that you can play in just a few minutes. Whether it’s a puzzle, an arcade game, or a casual challenge, these games provide a refreshing escape without taking up too much time.",
    },
    {
        name: "Sports",
        icon: "/icons/category/sports.svg",
        tag: "sports",
        description:
            "Get in the game with our exciting sports category! Play realistic and arcade-style sports games, including football, basketball, tennis, racing, and more. Whether you prefer solo challenges or multiplayer competition, these games deliver fast-paced action and thrilling sports experiences.",
    },
    {
        name: "Multiplayer",
        icon: "/icons/category/multiplayer.svg",
        tag: "multiplayer",
        description:
            "Connect with players from around the world in engaging multiplayer games. Team up, compete, chat on audio or challenge friends in a variety of online multiplayer experiences, from action-packed battles to cooperative puzzles. Enjoy the thrill of real-time gaming with endless social and competitive fun.",
    },
];
export default function GameSearchPage() {
    const [query, setQuery] = useState('');
    const [category, setCategory] = useState('all-games');
    const [page, setPage] = useState(1);
    const [pageSize] = useState(20);
    const [results, setResults] = useState([]);
    const [totalPages, setTotalPages] = useState(1);
    const [loading, setLoading] = useState(false);
    const location = useLocation()
    const searchParams = new URLSearchParams(location.search)
    const access_token = searchParams.get("_portalToken")

    const observer = useRef();
    const lastElementRef = useCallback(
        (node) => {
            if (loading) return;
            if (observer.current) observer.current.disconnect();
            observer.current = new IntersectionObserver((entries) => {
                if (entries[0].isIntersecting && page < totalPages) {
                    setPage((prev) => prev + 1);
                }
            });
            if (node) observer.current.observe(node);
        },
        [loading, page, totalPages]
    );

    const theme = process.env.REACT_APP_THEME;
    const isDark = theme === "dark";

    useEffect(() => {
        setResults([]);
        setPage(1);
    }, [category, query]);

    useEffect(() => {
        fetchGames();
    }, [page, category, query]);
    const controllerRef = useRef(null);

    const fetchGames = async () => {
        setLoading(true);

        // Cancel previous request if it exists
        if (controllerRef.current) {
            controllerRef.current.abort();
        }

        const controller = new AbortController();
        controllerRef.current = controller;

        try {
            const response = await axios.get(process.env.REACT_APP_API_SERVER_URL + `/game/search?access_token=${access_token}`, {
                params: {
                    query,
                    category,
                    p: page,
                    'page-size': pageSize,
                },
                headers: {
                    'client-id': process.env.REACT_APP_CLIENT_ID
                },
                signal: controller.signal,
            });

            const data = response.data;
            setResults((prev) => page === 1 ? data.results : [...prev, ...data.results]);
            setTotalPages(data.total_page);
        } catch (error) {
            if (axios.isCancel(error)) {
                console.log('Previous request canceled');
            } else {
                console.error('Error fetching games:', error);
            }
        } finally {
            setLoading(false);
        }
    };


    return (
        <div
            className={`min-h-[100vh] overflow-scroll mx-auto ${isDark ? 'bg-black text-white' : 'bg-white text-black'} relative`}>
            {process.env.REACT_APP_ENABLE_HEADER === "true" &&
                <header
                    className={`max-w-[960px] pt-[20px] mx-auto lg:px-0 px-2 flex items-start justify-between ${process.env.REACT_APP_THEME === "dark" ? "bg-black" : "bg-white"}`}>
                    {process.env.REACT_APP_BRAND_LOGO !== "TP" && <img
                        src={`/${process.env.REACT_APP_BRAND_LOGO}.png`}
                        alt="Brand Logo"
                        className="h-8 md:h-10 w-auto object-contain rounded-xl"
                    />}

                    {process.env.REACT_APP_BRAND_LOGO === "TP" && process.env.REACT_APP_THEME === "dark" &&
                        <a href={"https://timepass.app/yLpY/32natroq"} target={"_blank"}>
                            <img
                                src={`/logo-white.png`}
                                alt="Brand Logo"
                                className="h-8 md:h-10 w-auto object-contain rounded-xl"
                            />
                        </a>}
                    {process.env.REACT_APP_BRAND_LOGO === "TP" && process.env.REACT_APP_THEME === "light" &&
                        <a href={"https://timepass.app/yLpY/32natroq"} target={"_blank"}>
                            <img
                                src={`/logo-black.png`}
                                alt="Brand Logo"
                                className="h-8 md:h-10 w-auto object-contain rounded-xl"
                            />
                        </a>}
                </header>
            }
            <form onSubmit={(e) => e.preventDefault()}
                  className="max-w-[960px] px-2 mb-4 flex items-center justify-center gap-2 mx-auto mt-4">
                <Link href="/" to={`/${access_token ? '?_portalToken=' + access_token : ''}`}>
                    <button
                        type="button"
                        className={`left-[20px] top-[15px] flex items-center justify-center p-2 rounded-full outline-none hover:scale-105
            ${
                            process.env.REACT_APP_THEME === 'light'
                                ? 'bg-gray-700 hover:bg-black'
                                : 'bg-gray-700 hover:bg-gray-800'
                        }
            focus:outline-none  transition`}
                        aria-label="Search"
                    >
                        <img
                            src="https://timepass.games/icons/back-btn.svg"
                            alt="Search"
                            className="w-5 h-5"
                            draggable="false"
                        />
                    </button>
                </Link>

                <input
                    type="text"
                    value={query}
                    onChange={(e) => setQuery(e.target.value.trimStart())}
                    placeholder="Search games..."
                    className="border p-2 rounded-xl w-full text-black outline-none"
                />
            </form>

            <div
                className="max-w-[960px] flex gap-2 sm:flex-wrap sm:justify-center scrollbar-hide px-2 pb-3 pt-1 w-[90%] mx-auto overflow-x-scroll"
                style={{
                    scrollbarWidth: 'none',
                }}>
                {Categories.map((c, index) => (
                    <button
                        key={index}
                        className={`flex items-center gap-2 px-4 py-2 whitespace-nowrap rounded-full text-white text-sm transition transform hover:scale-105 ${
                            category === c.tag
                                ? "border-2 border-pink-300"
                                : "border-2 border-gray-500"
                        }`}
                        style={{
                            backgroundColor:
                                category === c.tag
                                    ? "#2c1b18"
                                    : "transparent",
                        }}
                        onClick={() => setCategory(c.tag)}
                    >
                        <img
                            draggable="false"
                            src={c.icon}
                            alt={`${c.name} icon`}
                            className="w-4 h-4"
                        />
                        <span
                            style={{
                                color:
                                    category === c.tag
                                        ? "white"
                                        : theme === "dark"
                                            ? "white"
                                            : "black",
                            }}
                        >
        {c.name}
    </span>
                    </button>
                ))}
            </div>
            <div className="max-w-[960px] px-2 flex flex-wrap gap-[10px] mx-auto justify-center">
                {results.map((game, index) => {
                    const isLast = index === results.length - 1;
                    return (
                        <Link to={`/${game.slug}${access_token ? '?_portalToken=' + access_token : ''}`} key={game.slug} ref={isLast ? lastElementRef : null}>
                            <img
                                src={game.property.thumbnail || game.property.image}
                                alt={game.property.name}
                                className="h-[100px] w-[100px] md:w-[150px] md:h-[150px] object-cover rounded"
                            />
                        </Link>
                    );
                })}
            </div>

            {loading && (
                <div className="text-center py-4">
                    <span>Loading more games...</span>
                </div>
            )}
            {!loading && results.length === 0 && (
                <div className="text-center py-4">
                    <span>No games available.</span>
                </div>
            )}
        </div>
    );
}
